import { ITeamScores } from "../../../types/types";
import { TeamLogo } from "../../shared/TeamLogo";
import { useRegularSeasonScoreboard } from "../hooks/useRegularSeasonScoreboard";

interface RegularSeasonScoreboardProps {
  teams?: ITeamScores[];
}

const gridCols =
  "grid w-full grid-cols-[3.5rem_minmax(0,1fr)_minmax(5.5rem,7.5rem)_minmax(5.5rem,7.5rem)_minmax(4.75rem,6.5rem)_minmax(4.75rem,6.5rem)] items-center gap-x-[clamp(0.65rem,1.5vw,1.25rem)] px-4 box-border max-md:grid-cols-[1.5rem_2rem_repeat(4,minmax(0,1fr))] max-md:gap-x-1 max-md:px-2";

const headerCol =
  "fh-table-head flex min-w-0 items-center opacity-90 whitespace-nowrap max-md:text-[clamp(0.48rem,2.1vw,0.58rem)] max-md:tracking-normal max-md:normal-case max-md:leading-[1.1] max-md:text-right";

export const RegularSeasonScoreboard = ({ teams: teamsOverride }: RegularSeasonScoreboardProps) => {
  const { strings, sortedTeams } = useRegularSeasonScoreboard(teamsOverride);

  return (
    <section
      className="mx-auto flex w-full max-w-[80rem] flex-col box-border rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-[clamp(1.5rem,3vw,2.5rem)] max-md:p-4 max-md:px-2"
      aria-label={strings.SCOREBOARD.FPL_REGULAR_PLACEMENTS_TITLE}
    >
      <h2 className="fh-title-section mb-6 text-center max-md:mb-4">
        {strings.SCOREBOARD.FPL_REGULAR_PLACEMENTS_TITLE}
      </h2>

      <section className="flex w-full max-w-full flex-col gap-[0.6rem] overflow-x-clip">
        <div className={`${gridCols} mb-1 min-h-11`} role="row">
          <span className={`${headerCol} justify-center`}>{strings.SCOREBOARD.GRID_PLACEMENT}</span>
          <span
            className={`${headerCol} justify-start gap-4 max-md:justify-center max-md:gap-0 max-md:text-[0] max-md:leading-[0] max-md:overflow-hidden`}
          >
            {strings.SCOREBOARD.GRID_TEAM_NAME}
          </span>
          <span
            className={`${headerCol} justify-end overflow-hidden text-right text-light-blue opacity-100`}
            title={strings.SCOREBOARD.GRID_SERIES_WINS}
          >
            <span className="max-md:hidden">{strings.SCOREBOARD.GRID_SERIES_WINS}</span>
            <span className="hidden max-md:block max-w-full overflow-hidden text-ellipsis text-right">
              {strings.SCOREBOARD.GRID_SERIES_WINS_SHORT}
            </span>
          </span>
          <span
            className={`${headerCol} justify-end overflow-hidden text-right text-[#f87171] opacity-100`}
            title={strings.SCOREBOARD.GRID_SERIES_LOSSES}
          >
            <span className="max-md:hidden">{strings.SCOREBOARD.GRID_SERIES_LOSSES}</span>
            <span className="hidden max-md:block max-w-full overflow-hidden text-ellipsis text-right">
              {strings.SCOREBOARD.GRID_SERIES_LOSSES_SHORT}
            </span>
          </span>
          <span
            className={`${headerCol} justify-end overflow-hidden text-right text-light-blue opacity-100`}
            title={strings.SCOREBOARD.GRID_GAME_WINS}
          >
            <span className="max-md:hidden">{strings.SCOREBOARD.GRID_GAME_WINS}</span>
            <span className="hidden max-md:block max-w-full overflow-hidden text-ellipsis text-right">
              {strings.SCOREBOARD.GRID_GAME_WINS_SHORT}
            </span>
          </span>
          <span
            className={`${headerCol} justify-end overflow-hidden text-right text-[#f87171] opacity-100`}
            title={strings.SCOREBOARD.GRID_GAME_LOSSES}
          >
            <span className="max-md:hidden">{strings.SCOREBOARD.GRID_GAME_LOSSES}</span>
            <span className="hidden max-md:block max-w-full overflow-hidden text-ellipsis text-right">
              {strings.SCOREBOARD.GRID_GAME_LOSSES_SHORT}
            </span>
          </span>
        </div>

        {sortedTeams.length > 0 ? (
          sortedTeams.map((t, pos) => (
            <div
              key={t.id}
              className="list-none rounded-[var(--surface-card-radius-sm)] bg-[var(--surface-card-row-bg)]"
            >
              <ul className={`${gridCols} m-0 min-h-[4.5rem] list-none max-md:min-h-14`}>
                <li className="flex min-w-0 items-center justify-center">
                  <span className="font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-white max-md:text-[0.85rem]">
                    {pos + 1}
                  </span>
                </li>

                <li className="flex min-w-0 items-center justify-start gap-4 max-md:justify-center max-md:gap-0">
                  <TeamLogo
                    teamId={t.id}
                    alt={t.name}
                    className="h-[2.8rem] w-[2.8rem] shrink-0 object-contain max-md:h-8 max-md:w-8"
                    width={45}
                    height={45}
                  />
                  <span className="font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-white [overflow-wrap:anywhere] max-md:hidden">
                    {t.name}
                  </span>
                </li>

                <li className="flex min-w-0 items-center justify-end text-right">
                  <span className="w-full text-right font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-light-blue max-md:text-[0.85rem]">
                    {t.wins}
                  </span>
                </li>

                <li className="flex min-w-0 items-center justify-end text-right">
                  <span className="w-full text-right font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-[#f87171] max-md:text-[0.85rem]">
                    {t.losses}
                  </span>
                </li>

                <li className="flex min-w-0 items-center justify-end text-right">
                  <span className="w-full text-right font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-light-blue max-md:text-[0.85rem]">
                    {t.gameWins}
                  </span>
                </li>

                <li className="flex min-w-0 items-center justify-end text-right">
                  <span className="w-full text-right font-jetbrains text-[0.95rem] font-semibold leading-[1.2] text-[#f87171] max-md:text-[0.85rem]">
                    {t.gameLosses}
                  </span>
                </li>
              </ul>
            </div>
          ))
        ) : (
          <p className="m-0 py-4 text-center opacity-75">{strings.SCOREBOARD.NO_TEAMS_AVAILABLE}</p>
        )}
      </section>
    </section>
  );
};
